import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useAuthUser } from '../../hooks/useAuthUser';
import { useAuth } from '@clerk/clerk-react';
import { AppAuthContext } from '../../contexts/AppAuthContext';
import { buildApiBases } from '../../utils/apiRequest';
import { resolveSocketAuthPayload } from '../../utils/socketAuth';

const WEB_PUSH_SW_SCOPE = '/web-push/';

const BrowserNotificationBridge = () => {
	const { user } = useAuthUser();
	const { getToken } = useAuth();
	const { testAuthSession } = useContext(AppAuthContext);
	const API_URL = process.env.REACT_APP_API_URL;
	const [allowPush, setAllowPush] = useState(true);
	const [hasWebPushSubscription, setHasWebPushSubscription] = useState(false);

	const socketBaseUrl = useMemo(() => {
		const bases = buildApiBases(API_URL);
		return bases.find(base => !/\/api$/i.test(base)) || bases[0] || '';
	}, [API_URL]);

	const fetchPreferences = useCallback(async () => {
		if (!user?.id) return;
		try {
			const res = await axios.get(`${API_URL}/users/${user.id}/preferences`);
			setAllowPush(res?.data?.preferences?.pushNotifications !== false);
		} catch (error) {
			console.error('Failed to fetch notification preferences:', error.message);
		}
	}, [API_URL, user?.id]);

	useEffect(() => {
		fetchPreferences();
	}, [fetchPreferences]);

	useEffect(() => {
		if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
			setHasWebPushSubscription(false);
			return;
		}
		let cancelled = false;
		const loadSubscriptionState = async () => {
			try {
				const registration =
					(await navigator.serviceWorker.getRegistration(WEB_PUSH_SW_SCOPE)) ||
					(await navigator.serviceWorker.getRegistration());
				if (!registration) {
					setHasWebPushSubscription(false);
					return;
				}
				const subscription = await registration.pushManager.getSubscription();
				if (!cancelled) {
					setHasWebPushSubscription(Boolean(subscription));
				}
			} catch (_) {
				if (!cancelled) {
					setHasWebPushSubscription(false);
				}
			}
		};
		loadSubscriptionState();
		window.addEventListener('focus', loadSubscriptionState);
		document.addEventListener('visibilitychange', loadSubscriptionState);
		return () => {
			cancelled = true;
			window.removeEventListener('focus', loadSubscriptionState);
			document.removeEventListener('visibilitychange', loadSubscriptionState);
		};
	}, []);

	useEffect(() => {
		if (!user?.id || !socketBaseUrl || typeof window === 'undefined') return undefined;
		if (!('Notification' in window)) return undefined;
		let isCancelled = false;
		let socket = null;

		const onNewNotification = ({ notification }) => {
			if (!notification || notification.userID !== user.id) return;
			if (!allowPush) return;
			if (hasWebPushSubscription) return;
			if (document.visibilityState === 'visible') return;
			if (Notification.permission !== 'granted') return;

			const nativeNotif = new Notification('Calcium', {
				body: notification.message,
				tag: notification._id || `notif-${Date.now()}`,
				data: { link: notification.link || '/notifications' }
			});

			nativeNotif.onclick = () => {
				const target = notification.link || '/notifications';
				window.focus();
				window.location.href = target;
			};
		};

		const setupSocket = async () => {
			const authPayload = await resolveSocketAuthPayload({
				getToken,
				testAuthToken: testAuthSession?.token,
				userID: user.id
			});
			if (!authPayload || isCancelled) return;
			socket = io(socketBaseUrl, {
				transports: ['websocket', 'polling'],
				auth: authPayload
			});
			socket.on('notification:new', onNewNotification);
		};
		setupSocket();

		return () => {
			isCancelled = true;
			if (socket) {
				socket.off('notification:new', onNewNotification);
				socket.disconnect();
			}
		};
	}, [allowPush, getToken, hasWebPushSubscription, socketBaseUrl, testAuthSession?.token, user?.id]);

	return null;
};

export default BrowserNotificationBridge;
